import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

// ─── Flag registry ────────────────────────────────────────────────────────────
// Add a new key here each time a component enters the redesign pipeline.
// Set its default to `false` so existing users are unaffected until toggled.
export interface FeatureFlags {
  sidebar: boolean;
  // locksGrid: boolean;   ← example: uncomment when that work begins
}

export const FLAG_LABELS: Record<keyof FeatureFlags, string> = {
  sidebar: "Sidebar (Tailwind)",
};

const defaults: FeatureFlags = {
  sidebar: true,
};

// ─── Persistence ──────────────────────────────────────────────────────────────
const STORAGE_KEY = "livly_feature_flags";

function loadFlags(): FeatureFlags {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...defaults, ...JSON.parse(stored) };
  } catch {}
  return { ...defaults };
}

// ─── Context ──────────────────────────────────────────────────────────────────
interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  setFlag: (key: keyof FeatureFlags, value: boolean) => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: defaults,
  setFlag: () => {},
});

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlags>(loadFlags);

  const setFlag = (key: keyof FeatureFlags, value: boolean) => {
    setFlags((prev) => {
      const next = { ...prev, [key]: value };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, setFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
